<script setup>
import { computed } from 'vue';
import format from 'date-fns/format';
import { Line } from 'vue-chartjs';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';

const props = defineProps({
  collection: {
    type: Object,
    required: true,
  },
  chartOptions: {
    type: Object,
    default: () => ({}),
  },
});

ChartJS.register(
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
);

const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'start',
      labels: {
        boxHeight: 8,
        boxWidth: 8,
        usePointStyle: true,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
    },
  },
};

const options = computed(() => ({
  ...defaultOptions,
  ...props.chartOptions,
  plugins: {
    ...defaultOptions.plugins,
    ...props.chartOptions.plugins,
    legend: {
      ...defaultOptions.plugins.legend,
      ...props.chartOptions.plugins?.legend,
    },
  },
  scales: {
    ...defaultOptions.scales,
    ...props.chartOptions.scales,
    x: {
      ...defaultOptions.scales.x,
      ...props.chartOptions.scales?.x,
    },
    y: {
      ...defaultOptions.scales.y,
      ...props.chartOptions.scales?.y,
    },
  },
}));

const valueAt = (value, index, fallback) => {
  if (Array.isArray(value)) return value[index] ?? fallback;
  return value ?? fallback;
};

const todayIndex = computed(() => {
  const todayLabels = [
    format(new Date(), 'dd MMM'),
    format(new Date(), 'dd/MM'),
    format(new Date(), 'yyyy-MM-dd'),
  ];
  return props.collection.labels.findIndex(label =>
    todayLabels.includes(String(label))
  );
});

const collection = computed(() => ({
  ...props.collection,
  datasets: props.collection.datasets.map(dataset => ({
    borderWidth: 2,
    tension: 0.35,
    ...dataset,
    pointRadius: dataset.data.map((_, index) =>
      index === todayIndex.value ? 6 : valueAt(dataset.pointRadius, index, 2)
    ),
    pointHoverRadius: dataset.data.map((_, index) =>
      index === todayIndex.value
        ? 8
        : valueAt(dataset.pointHoverRadius, index, 4)
    ),
    pointBackgroundColor: dataset.data.map((_, index) =>
      index === todayIndex.value
        ? '#f59e0b'
        : valueAt(dataset.pointBackgroundColor, index, dataset.backgroundColor)
    ),
    pointBorderColor: dataset.data.map((_, index) =>
      index === todayIndex.value
        ? '#ffffff'
        : valueAt(dataset.pointBorderColor, index, dataset.borderColor)
    ),
    pointBorderWidth: dataset.data.map((_, index) =>
      index === todayIndex.value
        ? 2
        : valueAt(dataset.pointBorderWidth, index, 1)
    ),
  })),
}));
</script>

<template>
  <Line :data="collection" :options="options" />
</template>
